import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";

// A existência de credencial é perguntada ao NATIVO (isCredentialsSaved), não
// inferida de flag local. A flag `isKeychainEnabled` era um proxy que divergia
// da realidade num caso previsível: no iOS o Keychain SOBREVIVE à desinstalação
// do app, mas o localStorage do WebView não. Depois de reinstalar, a flag vinha
// vazia com as credenciais ainda lá — o app nunca oferecia biometria, pedia o
// opt-in de novo, e as credenciais antigas ficavam órfãs pra sempre.
//
// A flag continua sendo escrita porque outras partes do app podem lê-la e
// porque ela é barata de manter em sincronia, mas NÃO é mais quem decide.
//
// OPT_IN_DISMISSED continua sendo intenção do usuário ("disse não, não pergunte
// de novo") — isso o nativo não sabe responder.
//
// Credenciais reais ficam no Keychain (iOS) / EncryptedSharedPreferences
// (Android), via @capgo/capacitor-native-biometric — NUNCA no localStorage.
const ENABLED_FLAG_KEY = "isKeychainEnabled";
const OPT_IN_DISMISSED_KEY = "keychainOptInDismissed";

// Identifier opaco e estável usado como `server` no Keychain. Antes era
// `process.env.VUE_APP_EAD_URL`, que (a) muda entre staging e produção e (b)
// muda quando o produtor configura domínio whitelabel — deixando credenciais
// órfãs no Keychain nativo, inacessíveis. Identifier fixo resolve.
const KEYCHAIN_SERVER = "br.com.pagtrust.ead.keychain.v1";

// Códigos do @capgo/capacitor-native-biometric que indicam erro ESTRUTURAL
// (biometria indisponível no device — sensor quebrado, biometria removida,
// device sem permissão). Diferente de cancel/timeout do user, que é temporário
// e NÃO deve desabilitar a feature.
//
// 3 = BIOMETRIC_NOT_AVAILABLE
// 4 = BIOMETRIC_NOT_ENROLLED
const STRUCTURAL_ERROR_CODES = new Set([3, 4, '3', '4']);

export class KeychainHandler {
  // Promise da operação biométrica em flight. Se múltiplos callers chamam
  // simultaneamente (ex: auth/reAuthenticate + http.init.js no boot), todos
  // recebem a MESMA Promise — sem abrir 2 prompts biométricos em sequência.
  static _inFlightUnlock = null;

  // Abre o prompt biométrico nativo e resolve true/false. Dedupe garante que
  // chamadas concorrentes compartilham a mesma Promise.
  //
  // Tratamento de erro distingue:
  //   - Cancel/falha do user/timeout (temporário) → retorna false, MANTÉM
  //     a flag de enabled. User pode tentar de novo no próximo prompt.
  //   - Erro estrutural (sensor removido, biometria desinscrita) → retorna
  //     false E desabilita a feature. Só re-habilita via login email+senha.
  static async _unlockKeychain({ reason = "Salvar conta no dispositivo", description } = {}) {
    if (this._inFlightUnlock) return this._inFlightUnlock;

    const promise = NativeBiometric.verifyIdentity({
      title: "PagTrust EAD",
      reason,
      description: description || "Confirme com a biometria do dispositivo"
    })
      .then(() => true)
      .catch(err => {
        if (this._isStructuralBiometricError(err)) {
          // Biometria removida/inacessível no device — desabilita a feature
          // localmente. Próximo login email+senha re-habilita se voltar.
          this._clearLocalStorage();
        }
        return false;
      })
      .finally(() => {
        this._inFlightUnlock = null;
      });

    this._inFlightUnlock = promise;
    return promise;
  }

  /**
   * Há um prompt biométrico aberto NESTE momento?
   *
   * A rede de segurança do boot (main.js) usa isto pra não liberar a tela de
   * login por baixo do prompt: o `verifyIdentity` fica pendurado no tempo de
   * reação do usuário, não em I/O, e um timeout fixo não tem como saber disso.
   */
  static isUnlockInFlight() {
    return this._inFlightUnlock !== null;
  }

  static _isStructuralBiometricError(err) {
    if (!err) return false;
    const code = err.code != null ? err.code : err.errorCode;
    if (code == null) return false;
    return STRUCTURAL_ERROR_CODES.has(code);
  }

  static _clearLocalStorage() {
    localStorage.removeItem(ENABLED_FLAG_KEY);
  }

  static async getIsAvailable() {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      return (await NativeBiometric.isAvailable()).isAvailable;
    } catch {
      return false;
    }
  }

  /**
   * Tipo de biometria do device, pra UI falar o nome certo em vez de "digital
   * ou Face ID". Retorna 'face' | 'fingerprint' | 'iris' | 'multiple' | null.
   *
   * `null` = indisponível ou desconhecido; a UI deve cair no texto genérico.
   * DEVICE_CREDENTIAL (PIN/padrão) também vira null: não é biometria, e chamar
   * de "digital" seria mentira.
   */
  static async getBiometryKind() {
    if (!Capacitor.isNativePlatform()) return null;
    try {
      const { isAvailable, biometryType } = await NativeBiometric.isAvailable();
      if (!isAvailable) return null;
      switch (biometryType) {
        case 1: return 'fingerprint';        // TOUCH_ID
        case 2: return 'face';               // FACE_ID
        case 3: return 'fingerprint';        // FINGERPRINT (Android)
        case 4: return 'face';               // FACE_AUTHENTICATION (Android)
        case 5: return 'iris';               // IRIS_AUTHENTICATION
        case 6: return 'multiple';           // MULTIPLE
        default: return null;                // NONE / DEVICE_CREDENTIAL
      }
    } catch {
      return null;
    }
  }

  /**
   * Existe credencial gravada no Keychain/Keystore? Pergunta ao nativo — não
   * dispara prompt biométrico (verificado na versão 8.4.2; método existe desde
   * a 7.3.0, com implementação nas duas plataformas).
   */
  static async hasSavedCredentials() {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const { isSaved } = await NativeBiometric.isCredentialsSaved({
        server: KEYCHAIN_SERVER
      });
      return Boolean(isSaved);
    } catch {
      // Método ausente numa versão antiga do plugin, ou falha do nativo: cai
      // pro proxy antigo em vez de desabilitar a feature pra todo mundo.
      return Boolean(localStorage.getItem(ENABLED_FLAG_KEY));
    }
  }

  static async getIsEnabled() {
    if (!(await this.getIsAvailable())) return false;
    const saved = await this.hasSavedCredentials();
    // Reconcilia a flag com a realidade. Serve aos dois sentidos: limpa flag
    // órfã (credencial sumiu) e re-habilita depois de reinstalar no iOS, onde o
    // Keychain sobrevive e o localStorage não.
    if (saved) localStorage.setItem(ENABLED_FLAG_KEY, "true");
    else this._clearLocalStorage();
    return saved;
  }

  // True se o user dispensou o opt-in da biometria. Caller deve respeitar e
  // NÃO perguntar de novo. Reset acontece quando:
  //   - User explicitamente reabilita via Settings (futuro)
  //   - Credenciais são salvas com sucesso (clearOptInDismissed em setCredentials)
  //   - User faz logout (clearCredentials)
  static getIsOptInDismissed() {
    return Boolean(localStorage.getItem(OPT_IN_DISMISSED_KEY));
  }

  static markOptInDismissed() {
    localStorage.setItem(OPT_IN_DISMISSED_KEY, "true");
  }

  static clearOptInDismissed() {
    localStorage.removeItem(OPT_IN_DISMISSED_KEY);
  }

  /**
   * Salva credenciais via biometria. Caller é responsável pelo opt-in (modal
   * Vue ou similar) ANTES de chamar este método — este método não pergunta,
   * apenas grava (após confirmar com biometria nativa).
   *
   * Retorna uma das strings:
   *   - 'saved': gravou com sucesso, biometria habilitada
   *   - 'unavailable': plataforma não suporta biometria
   *   - 'cancelled': user cancelou prompt biométrico (não é erro real)
   *   - 'failed': erro ao gravar no Keychain (caller pode mostrar toast)
   *
   * Comportamento backward-compat: se feature JÁ HABILITADA, re-grava as
   * credenciais sem novo prompt (caso comum: user trocou senha e re-logou).
   */
  static async setCredentials({ email, password }) {
    if (!(await this.getIsAvailable())) return 'unavailable';

    // Se já habilitada, atualiza silenciosamente (re-login após troca de senha).
    // Se desabilitada, exige confirmação biométrica explícita pra ativar.
    const alreadyEnabled = await this.getIsEnabled();
    if (!alreadyEnabled) {
      const unlocked = await this._unlockKeychain({
        description: "Salvar conta para entrar com biometria"
      });
      if (!unlocked) return 'cancelled';
    }

    try {
      await NativeBiometric.setCredentials({
        server: KEYCHAIN_SERVER,
        username: email,
        password
      });
      localStorage.setItem(ENABLED_FLAG_KEY, "true");
      this.clearOptInDismissed();
      return 'saved';
    } catch {
      return 'failed';
    }
  }

  static async getCredentials() {
    // A ORDEM importa: confirmar que existe credencial ANTES de abrir o prompt.
    // Antes, com a flag local dizendo "habilitado" e o Keychain vazio, o app
    // pedia a digital do usuário e só depois falhava — ele autenticava à toa.
    // getIsEnabled já consulta o nativo, então o caso está coberto aqui.
    if (!(await this.getIsEnabled())) return null;
    if (!(await this._unlockKeychain())) return null;

    try {
      const credentials = await NativeBiometric.getCredentials({
        server: KEYCHAIN_SERVER
      });
      return { email: credentials.username, password: credentials.password };
    } catch {
      return null;
    }
  }

  // Logout: SEMPRE limpa flag local + tenta apagar Keychain nativo. Antes
  // checava getIsEnabled, então se a flag tivesse sido limpa (por erro
  // estrutural, dados do app limpos no Android, etc.) o Keychain ficava com
  // credenciais zumbi após logout. Agora best-effort em ambos os caminhos.
  static async clearCredentials() {
    this._clearLocalStorage();
    this.clearOptInDismissed();
    try {
      await NativeBiometric.deleteCredentials({ server: KEYCHAIN_SERVER });
    } catch {
      // best-effort
    }
  }
}
